import { useSyncExternalStore } from 'react';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../../core/color';
import { findLayerById } from '../../core/layerTree';
import { TEXT_ALIGN_OPTIONS, TEXT_FONT_OPTIONS } from '../../core/textFonts';
import type { TextAlign, TextLayer, RGBA } from '../../core/types';
import {
  editTextLayer,
  setTextLayerProps,
  updateTextTransient,
} from '../../state/textActions';
import { getTextEditSession, subscribeTextEdit } from '../../state/textEdit';
import { getActiveDocument, useAppStore, type PanelFlyoutId } from '../../state/store';
import { Checkbox, NumberInput, Select } from '../components/controls';
import { PanelEmbedProvider } from './Panel';

function applyText(
  layer: TextLayer,
  editing: boolean,
  patch: {
    content?: string;
    fontFamily?: string;
    fontSize?: number;
    align?: TextAlign;
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    color?: RGBA;
  },
) {
  if (patch.color) useAppStore.getState().setForeground(patch.color);
  if (editing) updateTextTransient(patch);
  else setTextLayerProps(layer.id, patch);
}

function CharacterBody({ layer }: { layer: TextLayer }) {
  const session = useSyncExternalStore(subscribeTextEdit, getTextEditSession, getTextEditSession);
  const editing = session?.layerId === layer.id;
  const fonts = TEXT_FONT_OPTIONS.some((f) => f.value === layer.fontFamily)
    ? TEXT_FONT_OPTIONS
    : [{ value: layer.fontFamily, label: layer.fontFamily }, ...TEXT_FONT_OPTIONS];
  const set = (patch: Parameters<typeof applyText>[2]) => applyText(layer, editing, patch);

  return (
    <div className="panel__body type-panel">
      <div className="field">
        <span className="field__label">Font</span>
        <Select
          value={layer.fontFamily}
          options={fonts}
          onChange={(fontFamily) => set({ fontFamily })}
        />
      </div>
      <div className="field">
        <span className="field__label">Size</span>
        <NumberInput
          value={Math.round(layer.fontSize)}
          min={4}
          max={1000}
          onChange={(fontSize) => set({ fontSize })}
        />
        <span className="field__suffix">px</span>
      </div>
      <div className="field">
        <span className="field__label">Color</span>
        <label
          className="toolbar__swatch"
          style={{
            position: 'relative',
            width: 44,
            height: 22,
            background: rgbaToCss(layer.color),
          }}
        >
          <input
            type="color"
            style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            value={rgbaToHex(layer.color)}
            onChange={(event) => set({ color: hexToRgba(event.target.value) })}
          />
        </label>
      </div>
      <Checkbox
        label="Bold"
        checked={layer.fontWeight === 'bold'}
        onChange={(next) => set({ fontWeight: next ? 'bold' : 'normal' })}
      />
      <Checkbox
        label="Italic"
        checked={layer.fontStyle === 'italic'}
        onChange={(next) => set({ fontStyle: next ? 'italic' : 'normal' })}
      />
      <label className="field field--stack">
        <span className="field__label">Content</span>
        <textarea
          className="text-input text-input--area"
          rows={3}
          value={layer.content}
          onChange={(event) => set({ content: event.target.value })}
        />
      </label>
      <button
        type="button"
        className="button button--small"
        disabled={editing}
        onClick={() => editTextLayer(layer.id)}
      >
        {editing ? 'Editing on canvas…' : 'Edit Text'}
      </button>
    </div>
  );
}

function ParagraphBody({ layer }: { layer: TextLayer }) {
  const session = useSyncExternalStore(subscribeTextEdit, getTextEditSession, getTextEditSession);
  const editing = session?.layerId === layer.id;
  const set = (align: TextAlign) => applyText(layer, editing, { align });

  return (
    <div className="panel__body type-panel">
      <div className="field">
        <span className="field__label">Align</span>
        <Select
          value={layer.align}
          options={TEXT_ALIGN_OPTIONS}
          onChange={set}
        />
      </div>
      <div className="type-panel__align-row">
        {TEXT_ALIGN_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`button button--icon${layer.align === option.value ? ' button--primary' : ''}`}
            title={option.label}
            onClick={() => set(option.value)}
          >
            {option.label[0]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Character / Paragraph flyout opened from the right rail. */
export function TypePanel({ mode }: { mode: Extract<PanelFlyoutId, 'character' | 'paragraph'> }) {
  const doc = useAppStore(getActiveDocument);
  const setPanelFlyout = useAppStore((state) => state.setPanelFlyout);
  const layer = doc?.activeLayerId ? findLayerById(doc.layers, doc.activeLayerId) : null;
  const text = layer?.kind === 'text' ? layer : null;

  return (
    <PanelEmbedProvider>
      <section className="panel panel--embedded">
        <div className="panel-stack__tabs">
          <button
            type="button"
            className={`panel-stack__tab${mode === 'character' ? ' panel-stack__tab--active' : ''}`}
            onClick={() => setPanelFlyout('character')}
          >
            Character
          </button>
          <button
            type="button"
            className={`panel-stack__tab${mode === 'paragraph' ? ' panel-stack__tab--active' : ''}`}
            onClick={() => setPanelFlyout('paragraph')}
          >
            Paragraph
          </button>
        </div>
        <div className="panel__embed-body">
          {!text ? (
            <div className="panel__body">
              <p className="dialog__note">Select a text layer to edit character settings.</p>
            </div>
          ) : mode === 'character' ? (
            <CharacterBody layer={text} />
          ) : (
            <ParagraphBody layer={text} />
          )}
        </div>
      </section>
    </PanelEmbedProvider>
  );
}
